import { useMemo, useState } from "react";
import AutoComplete, { TAutoCompleteProps } from "../AutoComplete";
import { countries } from "@/constants/countries";
import { Merge } from "@/@types/utils.ts";
import { useTranslation } from "react-i18next";

export type CountryType = {
  name: string;
  flag: string;
  code: string;
  dial_code: string;
};

type TProps = Merge<
  Partial<TAutoCompleteProps<CountryType>>,
  {
    value?: CountryType["code"];
    onChange: (data: CountryType) => void;
    showText?: boolean;
    placeholder?: string;
    error?: string | boolean;
  }
>;

function CountriesInput({
  onChange,
  value,
  showText = true,
  placeholder,
  error,
  ...restProps
}: Readonly<TProps>) {
  const [input, setInput] = useState("");
  const { t } = useTranslation("common");

  const options = useMemo(
    () =>
      countries.filter((item) =>
        item.name?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase()),
      ),
    [input],
  );

  const selectedCountry = useMemo(
    () => countries.find((item) => item.code === value) || ({} as CountryType),
    [countries, value],
  );

  return (
    <div className="w-full">
      <AutoComplete<CountryType>
        {...restProps}
        dropdownClassName="z-20"
        error={error}
        placeholder={placeholder}
        options={options}
        query={input}
        setQuery={(v) => {
          setInput(v);
        }}
        value={selectedCountry}
        renderValue={(value: CountryType) => (
          <DisplayCountry
            showText={showText}
            flag={value?.flag}
            name={value?.name}
          />
        )}
        renderOption={(data) => (
          <DisplayCountry flag={data?.flag} name={data?.name} />
        )}
        setValue={(v) => {
          onChange(v || ({} as CountryType));
          setInput("");
        }}
        noItem={<div className={"p-4"}>{t("no_country_found")}</div>}
      />
    </div>
  );
}

const DisplayCountry = ({
  flag,
  name,
  showText = true,
}: {
  flag?: string;
  name?: string;
  showText?: boolean;
}) => {
  return (
    <div className="flex items-baseline  gap-x-2">
      <div
        className="text-[16px] "
        style={{ fontFamily: "Twemoji Country Flags" }}
      >
        {flag}
      </div>
      {showText && <div className="dark:text-white">{name}</div>}
    </div>
  );
};

export default CountriesInput;
